import type { RequestFn } from '../client';
import type {
  AnalyticsScopeRequest,
  AnalyticsScopeResponse,
  ClientAnalyticsRequest,
  DevicesAnalyticsResponse,
  GeoAnalyticsResponse,
  DeviceDrillRequest,
  GeoDrillRequest,
  DrillResponse,
  SubscriptionAnalyticsRequest,
  SubscriptionAnalyticsResponse,
  SubscriptionDrillRequest,
  ScopeDrillRequest,
  AnalyticsExclusionRequest,
  AnalyticsExclusionResponse,
  AnalyticsExclusionsResponse,
} from '../types';

export class Analytics {
  constructor(private readonly request: RequestFn) {}

  /**
   * Scoped, drill-level analytics dashboard for the authenticated merchant
   * (the same engine as the admin portal, pinned server-side to your own
   * merchant). The server ignores `merchant_id` — it always scopes to your
   * merchant, and to your single shop for profile-scoped users — so pass only
   * `project_id` / `shop_id` to drill and the window / `sections` fields.
   * Returns one drill level: the scope's daily series + previous window,
   * processor mix and direct children. `GET /analytics/scope`
   */
  async scope(params?: AnalyticsScopeRequest): Promise<AnalyticsScopeResponse> {
    return this.request('GET', '/analytics/scope', {
      query: params as Record<string, string | number | boolean | undefined>,
    });
  }

  /**
   * Device analytics over the canonical client-context observation per
   * payment (browser/platform families, device classes and models, checkout
   * channel mix, time-to-pay), pinned server-side to your own merchant and
   * drillable via `project_id` / `shop_id` exactly like `scope`. Gated on the
   * client-context optimisation-use switch: when it is off the server answers
   * 200 with `enabled: false` and a caveat naming the switch.
   * `GET /analytics/devices`
   */
  async devices(params?: ClientAnalyticsRequest): Promise<DevicesAnalyticsResponse> {
    return this.request('GET', '/analytics/devices', {
      query: params as Record<string, string | number | boolean | undefined>,
    });
  }

  /**
   * Geo analytics over the canonical client-context observation per payment:
   * country totals, city bubbles (IP mode), buyer languages, buyer-local
   * purchase hours and the IP-vs-billing mismatch share. `mode` selects the
   * location claim (`ip` default, `billing`); the two are never coalesced.
   * Same drill, window and gating contract as `devices`.
   * `GET /analytics/geo`
   */
  async geo(params?: ClientAnalyticsRequest): Promise<GeoAnalyticsResponse> {
    return this.request('GET', '/analytics/geo', {
      query: params as Record<string, string | number | boolean | undefined>,
    });
  }

  /**
   * The recent payments behind one clicked geo target: a map country (in the
   * active claim mode), optionally narrowed to an IP-resolved city, or one
   * buyer-local heatmap cell (`dow` + `hour`, paid sessions only). Same
   * window/scope/filter and gating contract as `geo`; capped at 50 rows,
   * newest first, with the full match count alongside.
   * `GET /analytics/geo/transactions`
   */
  async geoTransactions(params: GeoDrillRequest): Promise<DrillResponse> {
    return this.request('GET', '/analytics/geo/transactions', {
      query: params as Record<string, string | number | boolean | undefined>,
    });
  }

  /**
   * The recent payments behind one clicked device target: exactly one of a
   * browser family, a platform family, an identified device-model label, or
   * a device class. Family/model targets are resolved server-side with the
   * same classifiers the cards use. Same window/scope/filter and gating
   * contract as `devices`; 50 rows per page (`offset` for the next page),
   * newest first, with the full match count alongside.
   * `GET /analytics/devices/transactions`
   */
  async deviceTransactions(params: DeviceDrillRequest): Promise<DrillResponse> {
    return this.request('GET', '/analytics/devices/transactions', {
      // A discriminated union carries no index signature, so the widening
      // goes via `unknown` — the union is the point.
      query: params as unknown as Record<string, string | number | boolean | undefined>,
    });
  }

  /**
   * The payments behind one clicked element of the payments dashboard: a
   * processor-donut slice, a payment-method slice, an outcome segment of a
   * series bar, a series bucket, or a breakdown row — combinable, with
   * `target_bucket` narrowing every other target. The outcome mapping is the
   * one the series and donuts are folded with, expanded server-side, so the
   * list is what the clicked number was made of. Rows and the whole-match
   * `summary` carry USD figures converted with the dashboard's own rates.
   * Same window/scope/chip contract as `scope`; sorted per `sort_on` /
   * `sort_by`, `limit` rows (default 50) per page, `offset` for the next.
   * `GET /analytics/scope/transactions`
   */
  async scopeTransactions(params: ScopeDrillRequest): Promise<DrillResponse> {
    return this.request('GET', '/analytics/scope/transactions', {
      // A discriminated union carries no index signature, so the widening
      // goes via `unknown` — the union is the point, and the query builder
      // only ever reads own enumerable keys.
      query: params as unknown as Record<string, string | number | boolean | undefined>,
    });
  }

  /**
   * Subscription analytics over `subscription` and `invoice`: estimated
   * recurring volume, the invoice funnel, movement (new / expansion /
   * contraction / churn), both processor axes, plan mix and the breakdown one
   * level below the scope. Pinned server-side to your own merchant and
   * drillable via `project_id` / `shop_id` exactly like `scope`.
   *
   * Half the figures are **stocks** — a snapshot at the window's end rather
   * than a sum over it — so `est_monthly_volume_usd` and `active` can match
   * across a 7-day and a 30-day window while `billed_volume_usd` does not.
   * Day granularity only. `GET /analytics/subscriptions`
   */
  async subscriptions(
    params?: SubscriptionAnalyticsRequest,
  ): Promise<SubscriptionAnalyticsResponse> {
    return this.request('GET', '/analytics/subscriptions', {
      query: params as Record<string, string | number | boolean | undefined>,
    });
  }

  /**
   * The billing cycles behind one clicked element of the subscription
   * dashboard: an invoice outcome, a processor slice on either axis, a plan
   * row, a subscription status, a movement component, a series bucket or a
   * breakdown row. Same window/scope/filter contract as `subscriptions`; 50
   * rows per page (`offset` for the next), newest first, with the full match
   * count alongside.
   *
   * A cycle that never reached a payment is listed too — that is what "still
   * unpaid" means — and carries its invoice id as `payment_id` with
   * `invoice_id` set to the same value, so you can always tell which you got.
   * `GET /analytics/subscriptions/list`
   */
  async subscriptionsList(params: SubscriptionDrillRequest): Promise<DrillResponse> {
    return this.request('GET', '/analytics/subscriptions/list', {
      // A discriminated union carries no index signature, so the widening
      // goes via `unknown` — the union is the point, and the query builder
      // only ever reads own enumerable keys.
      query: params as unknown as Record<string, string | number | boolean | undefined>,
    });
  }

  /**
   * Every analytics exclusion of the merchant, with the payments each one
   * matched in the lookback window.
   *
   * These rules change what the merchant's own dashboards count. Financial
   * records and transaction lists are never filtered by them.
   *
   * `matched_payments` counts overlap between rules: a payment matched by
   * three rules is counted once by each, so the values are not additive. The
   * envelope's `best_effort` and `lookback_days` apply to every row — matching
   * runs on client signals that may be absent or expired, within that window.
   * `GET /analytics/exclusions`
   */
  async listExclusions(): Promise<AnalyticsExclusionsResponse> {
    return this.request('GET', '/analytics/exclusions');
  }

  /**
   * Write a new exclusion. A shop-scoped user must name their own shop in
   * `profile_id`; omitting it applies the rule to every shop of the merchant.
   * Omitting `surfaces` means **all five**, not none.
   * `POST /analytics/exclusions`
   */
  async createExclusion(rule: AnalyticsExclusionRequest): Promise<AnalyticsExclusionResponse> {
    return this.request('POST', '/analytics/exclusions', { body: rule });
  }

  /**
   * Replace a stored exclusion **completely**. This is not a patch: every
   * field you leave out reverts to its default rather than keeping its stored
   * value, so send the whole rule — including an explicit `null` for a
   * `profile_id`, `value`, `valid_from`, `valid_until` or `note` you mean to
   * clear, and the full `surfaces` list you mean to keep.
   * `PUT /analytics/exclusions/{id}`
   */
  async updateExclusion(
    id: string,
    rule: AnalyticsExclusionRequest,
  ): Promise<AnalyticsExclusionResponse> {
    return this.request('PUT', `/analytics/exclusions/${encodeURIComponent(id)}`, { body: rule });
  }

  /**
   * Delete a stored exclusion. Returns the rule as it was, so a caller can
   * say what it removed. `DELETE /analytics/exclusions/{id}`
   */
  async deleteExclusion(id: string): Promise<AnalyticsExclusionResponse> {
    return this.request('DELETE', `/analytics/exclusions/${encodeURIComponent(id)}`);
  }

  /** Global search. `POST /analytics/search` */
  async search(params: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.request('POST', '/analytics/search', { body: params });
  }

  /** Domain-specific search. `POST /analytics/search/{domain}` */
  async searchDomain(
    domain: string,
    params: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request('POST', `/analytics/search/${encodeURIComponent(domain)}`, {
      body: params,
    });
  }

  /** Get analytics info. `GET /analytics/{domain}/info` */
  async getInfo(domain: string): Promise<Record<string, unknown>> {
    return this.request('GET', `/analytics/${encodeURIComponent(domain)}/info`);
  }

  /** Get API event logs. `GET /analytics/api-event-logs` */
  async apiEventLogs(
    params?: Record<string, string | number | undefined>,
  ): Promise<Record<string, unknown>> {
    return this.request('GET', '/analytics/api-event-logs', { query: params });
  }

  /** Get SDK event logs. `POST /analytics/sdk-event-logs` */
  async sdkEventLogs(params: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.request('POST', '/analytics/sdk-event-logs', { body: params });
  }

  /** Get connector event logs. `GET /analytics/connector-event-logs` */
  async connectorEventLogs(
    params?: Record<string, string | number | undefined>,
  ): Promise<Record<string, unknown>> {
    return this.request('GET', '/analytics/connector-event-logs', { query: params });
  }

  /** Get routing event logs. `GET /analytics/routing-event-logs` */
  async routingEventLogs(
    params?: Record<string, string | number | undefined>,
  ): Promise<Record<string, unknown>> {
    return this.request('GET', '/analytics/routing-event-logs', { query: params });
  }

  /** Get outgoing webhook event logs. `GET /analytics/outgoing-webhook-event-logs` */
  async outgoingWebhookEventLogs(
    params?: Record<string, string | number | undefined>,
  ): Promise<Record<string, unknown>> {
    return this.request('GET', '/analytics/outgoing-webhook-event-logs', { query: params });
  }
}
